import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { History } from 'lucide-react';
import { useMetricsHistory } from '../../hooks/useMetricsHistory';
import { formatCompactNumber } from '@/lib/utils/formatNumber';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MetricsHistoryPopoverProps {
  contentSubmissionId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  iconOnly?: boolean;
}

export const MetricsHistoryPopover: React.FC<MetricsHistoryPopoverProps> = ({
  contentSubmissionId,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  iconOnly = false,
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const { data: history = [], isLoading } = useMetricsHistory(
    open ? contentSubmissionId : null
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {iconOnly ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 bg-white hover:bg-slate-50 border border-slate-200 text-[#94A3B8] hover:text-[#64748B] rounded-xl flex items-center justify-center transition-colors shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none cursor-pointer"
                aria-label="History"
              >
                <History className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>History</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
          >
            <History className="h-3 w-3" />
            History
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent className="w-80 p-3" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between border-b pb-2">
            <h4 className="text-sm font-semibold">Metrics History</h4>
            <span className="text-xs text-muted-foreground">
              {history.length} {history.length === 1 ? 'snapshot' : 'snapshots'}
            </span>
          </div>

          {isLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-8 rounded" />
              <Skeleton className="h-8 rounded" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-4">
              No historical snapshots yet. Metrics are recorded automatically when scraped or updated.
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map((entry) => {
                const metrics = entry.performance_metrics;
                const dateFormatted = entry.snapshot_at
                  ? format(new Date(entry.snapshot_at), 'MMM d, yyyy h:mm a')
                  : 'Unknown date';

                return (
                  <div
                    key={entry.id}
                    className="text-xs bg-muted/40 rounded p-2 space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">
                        {dateFormatted}
                      </span>
                      <Badge variant="outline" className="text-[10px] px-1 py-0 capitalize">
                        {entry.extraction_source}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1 text-muted-foreground text-[11px] pt-1">
                      <div>V: {formatCompactNumber(metrics?.views)}</div>
                      <div>L: {formatCompactNumber(metrics?.likes)}</div>
                      <div>C: {formatCompactNumber(metrics?.comments)}</div>
                      <div>S: {formatCompactNumber(metrics?.shares)}</div>
                    </div>
                    {entry.notes && (
                      <div className="text-[10px] text-muted-foreground italic pt-0.5">
                        {entry.notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
